import type { CSSProperties, ReactElement, ReactNode } from "react";
import React, { useState } from "react";

import SelectorIcon from "@assets/icons/selector.svg";

import "./ButtonSelectorWithCustomSelect.css";

import { cn } from "@utils/tailwind.util";
import { Button } from "@ui/Button/Button";
import { EmptyControl, SelectProps } from "@ui/Select";

interface Props {
  className?: string;
  children: ReactElement;
  label: ReactNode | string;
  menuWidth?: CSSProperties["width"];
  badgeStyles?: CSSProperties;
  badgeClassName?: string;
  labelClassName?: string;
  hideSuffixIcon?: boolean;
  isDisabled?: boolean;
  menuClassName?: string;
  selectProps?: SelectProps<any, any>;
}

export function ButtonSelectorWithCustomSelect({
  className = "dropdown",
  children,
  label,
  menuWidth = 200,
  badgeStyles = {},
  badgeClassName = "",
  labelClassName = "",
  hideSuffixIcon = false,
  isDisabled = false,
  menuClassName = "",
  selectProps,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dropdown
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      className={className}
      menuWidth={menuWidth}
      menuClassName={menuClassName}
      target={
        <Button
          buttonType="SECONDARY"
          className={cn(
            "h-auto px-1 py-0.5 dark:bg-gray-700 dark:hover:bg-gray-600",
            badgeClassName
          )}
          SuffixIcon={hideSuffixIcon ? undefined : SelectorIcon}
          SuffixIconClass="text-gray-550 dark:text-invertedPrimary80 flex-shrink-0"
          strokeWidthIcon={2}
          style={badgeStyles}
          onClick={() => setIsOpen((prev) => !prev)}
          isDisabled={isDisabled}
          disableActiveState
        >
          <div className={cn("ml-1 text-xs font-medium text-content-primary", labelClassName)}>
            {label}
          </div>
        </Button>
      }
    >
      {React.cloneElement(children, {
        menuIsOpen: true,
        autoFocus: true,
        components: {
          DropdownIndicator: null,
          IndicatorSeparator: null,
          Control: EmptyControl,
          ClearIndicator: undefined,
        },
        controlShouldRenderValue: false,
        onMenuClose: () => setIsOpen(false),
        ...selectProps,
        isSearchable: false,
      })}
    </Dropdown>
  );
}

const Menu = (props: JSX.IntrinsicElements["div"]) => (
  <div
    className={cn("button-selector-with-custom-select__menu absolute z-50", props.className)}
    {...props}
  />
);

const Blanket = (props: JSX.IntrinsicElements["div"]) => <div className="blanket" {...props} />;

export const Dropdown = ({
  children,
  isOpen,
  target,
  onClose,
  className,
  menuWidth,
  menuClassName,
}: {
  children?: ReactNode;
  readonly isOpen: boolean;
  readonly target: ReactNode;
  readonly onClose: () => void;
  readonly className: string;
  readonly menuWidth: Props["menuWidth"];
  readonly menuClassName?: string;
}) => (
  <div className={className}>
    {target}
    {isOpen ? (
      <Menu style={{ width: menuWidth }} className={menuClassName}>
        {children}
      </Menu>
    ) : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
);
